import { create } from 'zustand';

import { SlotAttendanceRepository } from '../../api/repository/slot-attendance';
import type { SlotAttendanceParams } from '../../api/repository/slot-attendance';
import type { QltSlotAttendance } from '../../api/types';
import { userErrorMessage } from '../userError';
import { EMPTY_COUNTS, defaultDraft, deriveCounts, normalizeDraft, pendingChanges, sameAttendanceContext } from './attendance-rules';
import type { AttendanceCounts, AttendanceDraft } from './attendance-rules';

export type { AttendanceCounts, AttendanceDraft } from './attendance-rules';

// Every load() is sequenced. The roster endpoint takes seconds, StrictMode fires the panel effect
// twice and the modal loads again on open, so a slow earlier response used to land last and wipe
// the defaulted draft. Only the newest request may write.
let loadSeq = 0;

export interface QltSlotAttendanceState {
  rows: QltSlotAttendance[];
  rowsById: Record<string, QltSlotAttendance>;
  draft: Record<string, AttendanceDraft>;
  counts: AttendanceCounts;
  dirty: boolean;
  context: SlotAttendanceParams | null;
  loading: boolean;
  saving: boolean;
  error: string | null;
  load: (params: SlotAttendanceParams) => Promise<QltSlotAttendance[]>;
  setDraft: (id: string, value: AttendanceDraft) => void;
  markAllPresent: () => void;
  save: () => Promise<boolean>;
  clearError: () => void;
  reset: () => void;
}

export const useQltSlotAttendanceStore = create<QltSlotAttendanceState>((set, get) => ({
  rows: [],
  rowsById: {},
  draft: {},
  counts: { ...EMPTY_COUNTS },
  dirty: false,
  context: null,
  loading: false,
  saving: false,
  error: null,

  async load(params) {
    loadSeq += 1;
    const seq = loadSeq;
    set({ loading: true, error: null, context: params });
    try {
      const rows = await SlotAttendanceRepository.list(params);
      if (seq !== loadSeq) return get().rows;
      const unique: QltSlotAttendance[] = [];
      const rowsById: Record<string, QltSlotAttendance> = {};
      const seenStudents: Record<string, boolean> = {};
      for (let i = 0; i < rows.length; i += 1) {
        const row = rows[i];
        const studentId = String(row.student._id || row.student.id || '');
        if (!studentId || seenStudents[studentId]) continue;
        seenStudents[studentId] = true;
        rowsById[row._id] = row;
        unique.push(row);
      }
      // 'vi-VN' or Đ is folded into D and Đăng lands in front of Dung
      unique.sort((a, b) => (a.student.nameToNumber || a.student.name || '').localeCompare(b.student.nameToNumber || b.student.name || '', 'vi-VN'));
      // the default lands in the same set() as the rows, so the roster can never render unselected
      const draft = defaultDraft(unique);
      set({ rows: unique, rowsById, draft, counts: deriveCounts(unique, draft), dirty: Object.keys(draft).length > 0, loading: false });
      return unique;
    } catch (error) {
      if (seq !== loadSeq) return get().rows;
      set({ loading: false, error: userErrorMessage(error) });
      return [];
    }
  },

  setDraft(id, value) {
    const state = get();
    const draft = { ...state.draft, [id]: normalizeDraft(value) };
    set({ draft, counts: deriveCounts(state.rows, draft), dirty: true });
  },

  markAllPresent() {
    const state = get();
    const draft = { ...state.draft };
    for (let i = 0; i < state.rows.length; i += 1) {
      draft[state.rows[i]._id] = { status: 'present', subStatus: 'onTime' };
    }
    set({ draft, counts: deriveCounts(state.rows, draft), dirty: state.rows.length > 0 });
  },

  async save() {
    const state = get();
    const context = state.context;
    if (!context) return true;
    // The diff is the only authority. A defaulted roster differs from the server, so it is written.
    const listChanges = pendingChanges(state.rowsById, state.draft);
    if (listChanges.length === 0) {
      set({ dirty: false });
      return true;
    }
    set({ saving: true, error: null });
    try {
      const result = await SlotAttendanceRepository.updateMany({
        listChanges,
        indexDay: context.indexDay,
        date: context.date,
      });
      const resultShape = result as { nModified?: number; modifiedCount?: number };
      const modified = Number(resultShape.nModified ?? resultShape.modifiedCount ?? 0);
      // another period was opened meanwhile: its roster and error slot are not this save's to touch
      if (!sameAttendanceContext(get().context, context)) {
        set({ saving: false });
        return modified >= 1;
      }
      if (modified < 1) {
        set({ saving: false, error: 'Không có bản ghi điểm danh nào được cập nhật. Vui lòng tải lại danh sách.' });
        return false;
      }
      await get().load(context);
      set({ saving: false });
      return true;
    } catch (error) {
      set(sameAttendanceContext(get().context, context) ? { saving: false, error: userErrorMessage(error) } : { saving: false });
      return false;
    }
  },

  clearError: () => set({ error: null }),

  reset: () => set({ rows: [], rowsById: {}, draft: {}, counts: { ...EMPTY_COUNTS }, dirty: false, context: null, error: null }),
}));
